"use client";

import React from "react";
import { ArrowRight, CircleAlert as AlertCircle, Droplets, Hourglass, UtensilsCrossed, Bath, ShowerHead, GitMerge } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
interface DrainIssue {
  id: string;
  title: string;
  description: string;
  iconName: string;
}
const drainIssues: DrainIssue[] = [{
  id: "clogged-drain",
  title: "Clogged Drain",
  description: "Complete water stoppage with standing water that will not drain down the pipe.",
  iconName: "droplets"
}, {
  id: "slow-drain",
  title: "Slow Drain",
  description: "Water pools and drains at a sluggish pace, indicating progressive buildup inside the line.",
  iconName: "hourglass"
}, {
  id: "kitchen-sink",
  title: "Kitchen Sink Clogged",
  description: "Grease, food particles, and soap sediment blocking the kitchen disposal or trap.",
  iconName: "utensils"
}, {
  id: "bathroom-sink",
  title: "Bathroom Sink Clogged",
  description: "Hair, toothpaste, and mineral scale restricting standard basin water clearance.",
  iconName: "bath"
}, {
  id: "shower-drain",
  title: "Shower Drain Clogged",
  description: "Standing water around your feet caused by deep hair mats and soap residue in the shower trap.",
  iconName: "shower"
}, {
  id: "multiple-drains",
  title: "Multiple Drains Clogged",
  description: "Simultaneous backing up across fixtures, signalling an urgent main sewer line obstruction.",
  iconName: "git-merge"
}];
function ProblemIcon({
  name
}: {
  name: string;
}) {
  const iconProps = {
    className: "size-6 text-primary"
  };
  switch (name) {
    case "hourglass":
      return <Hourglass {...iconProps} />;
    case "utensils":
      return <UtensilsCrossed {...iconProps} />;
    case "bath":
      return <Bath {...iconProps} />;
    case "shower":
      return <ShowerHead {...iconProps} />;
    case "git-merge":
      return <GitMerge {...iconProps} />;
    case "droplets":
    default:
      return <Droplets {...iconProps} />;
  }
}
export default function DrainProblems() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="drainproblems" data-nav="light" className="bg-background text-foreground py-16 sm:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="flex flex-col items-center text-center max-w-3xl mx-auto mb-12 sm:mb-16">
          <Badge variant="secondary" className="mb-4 px-3.5 py-1 text-xs font-semibold tracking-wider uppercase rounded-full bg-secondary text-secondary-foreground border-border">
            Diagnostic Category
          </Badge>
          
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight mb-4">
            Drain Problems
          </h2>

          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl font-default">
            Identify your drainage symptom below for rapid triage. Jonesboro Emergency Plumber provides fast clearing, hydro-jetting, and rooter solutions across Jonesboro, GA.
          </p>
        </div>

        {/* Diagnostic Problem Cards Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12 sm:mb-16">
          {drainIssues.map((issue, index) => <motion.div key={issue.id} data-index={index} variants={itemVariants} className="h-full">
              <Link to="/drain-cleaning/" className="group block h-full focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-xl">
                <Card className="h-full bg-card hover:bg-muted/50 border border-border transition-[box-shadow,background-color] duration-200 shadow-sm hover:shadow-md rounded-xl overflow-hidden p-6 flex flex-col justify-between">
                  <CardContent className="p-0 flex flex-col items-start gap-4">
                    <div className="flex items-center justify-between w-full">
                      <div className="size-12 rounded-xl bg-secondary flex items-center justify-center text-primary group-hover:scale-105 transition-transform duration-200">
                        <ProblemIcon name={issue.iconName} />
                      </div>
                      <span className="size-8 rounded-full bg-background border border-border flex items-center justify-center text-muted-foreground group-hover:text-primary group-hover:border-primary/40 group-hover:translate-x-1 transition-all duration-200">
                        <ArrowRight className="size-4" />
                      </span>
                    </div>

                    <div className="space-y-2 mt-2">
                      <h3 className="text-xl font-default font-semibold text-card-foreground group-hover:text-primary transition-colors">
                        {issue.title}
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed font-default">
                        {issue.description}
                      </p>
                    </div>
                  </CardContent>

                  <div className="mt-6 pt-4 border-t border-border flex items-center justify-between text-xs font-medium text-muted-foreground group-hover:text-foreground">
                    <span className="inline-flex items-center gap-1.5">
                      <AlertCircle className="size-3.5 text-accent" />
                      Immediate dispatch ready
                    </span>
                    <span className="font-semibold text-primary">
                      Diagnose
                    </span>
                  </div>
                </Card>
              </Link>
            </motion.div>)}
        </motion.div>

        {/* Centered CTA Section */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-center">
          <Button asChild size="lg" className="rounded-full px-8 py-6 text-base font-semibold shadow-md hover:shadow-lg transition-all">
            <Link to="/drain-cleaning/" className="inline-flex items-center gap-2">
              <span>View Drain Problems</span>
              <ArrowRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}